import { useQuery } from 'react-query'
import {
  diffCatalogues,
  shapeFromCatalogue,
  shapeFromManifest,
} from '../../shared/catalogue-diff'
import type { CatalogueDiff } from '../../shared/catalogue-diff'
import { shouldRetry } from '../../shared/retry'
import type { AvatarBoClient, BoCatalog } from '../../shared/types'

export const diffKeys = {
  manifest: (n: number) => ['avatar-bo', 'manifest', n] as const,
}

export interface UseDraftDiffOpts {
  client: AvatarBoClient
  setId?: string
  /** The staged version to compare the draft with. Absent means there is nothing to compare. */
  version?: number
  catalogue?: BoCatalog
}

export interface DraftDiff {
  diff?: CatalogueDiff
  isLoading: boolean
  isError: boolean
}

/**
 * How the draft differs from one staged version, computed from that version's MANIFEST — not the
 * player config, which drops tier rules and paint order. Called against LIVE for the number an
 * operator sees, and against the newest staged version to tell "new work" from "already passed".
 */
export function useDraftDiff({
  client,
  setId,
  version,
  catalogue,
}: UseDraftDiffOpts): DraftDiff {
  const enabled = Boolean(setId) && version !== undefined
  const staged = useQuery(
    diffKeys.manifest(version ?? 0),
    () => client.getVersionManifest(version as number),
    { enabled, refetchOnWindowFocus: false, retry: shouldRetry },
  )

  if (!enabled || !catalogue || !staged.data) {
    return {
      diff: undefined,
      isLoading: enabled && staged.isLoading,
      isError: staged.isError,
    }
  }

  return {
    diff: diffCatalogues(
      shapeFromManifest(staged.data, setId as string),
      shapeFromCatalogue(catalogue),
    ),
    isLoading: false,
    isError: false,
  }
}
